import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';
import '../../styles/AddArtworkPage.css';

const AddArtworkPage = () => {
  const navigate = useNavigate();
  const { userId } = useParams(); // Get the artist's user ID from the URL
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [artist, setArtist] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    title: '',
    medium: '', // Will be stored in description
    width: '',
    height: '',
    year: new Date().getFullYear(),
    price: '',
    status: 'For Sale',
    location: null, // Will be gallery_id (integer)
    process: '',
    inspiration: '',
    cover_image: null
  });

  // Media elements
  const [audioFile, setAudioFile] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [audioUploading, setAudioUploading] = useState(false);

  // Drag and drop states
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [isDraggingAdditional, setIsDraggingAdditional] = useState(false);
  const [dragCounterAdditional, setDragCounterAdditional] = useState(0);

  // Gallery selection
  const [galleries, setGalleries] = useState([]);
  const [gallerySearch, setGallerySearch] = useState('');
  const [showGalleryDropdown, setShowGalleryDropdown] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchArtistInfo();
    }
    loadGalleries();

    // Handle click outside to close dropdown
    const handleClickOutside = (event) => {
      if (!event.target.closest('.gallery-search-container')) {
        setShowGalleryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userId]);

  const loadGalleries = async () => {
    try {
      const { data, error } = await supabase
        .from('galleries')
        .select('gallery_id, name, type, address')
        .eq('approved', true)
        .order('name');

      if (error) throw error;
      setGalleries(data || []);
    } catch (error) {
      console.error('Error loading galleries:', error);
    }
  };

  const fetchArtistInfo = async () => {
    try {
      console.log('Fetching artist info for userId:', userId);
      
      const { data, error } = await supabase
        .from('users')
        .select('uuid, first_name, last_name, email')
        .eq('uuid', userId)
        .single();

      console.log('Supabase response:', { data, error });

      if (error) throw error;
      setArtist(data);
    } catch (error) {
      console.error('Error fetching artist info:', error);
      setMessage({ type: 'error', text: `Failed to load artist information: ${error.message}` });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGallerySelect = (gallery) => {
    setSelectedGallery(gallery);
    setFormData(prev => ({
      ...prev,
      location: gallery.gallery_id
    }));
    setGallerySearch(gallery.name);
    setShowGalleryDropdown(false);
  };

  const handleGallerySearch = (value) => {
    setGallerySearch(value);
    setShowGalleryDropdown(true);
    console.log('Gallery search:', value, 'Show dropdown:', true);
    if (!value) {
      setSelectedGallery(null);
      setFormData(prev => ({
        ...prev,
        location: null
      }));
    }
  };

  const filteredGalleries = galleries.filter(gallery =>
    gallery.name.toLowerCase().includes(gallerySearch.toLowerCase()) ||
    gallery.type.toLowerCase().includes(gallerySearch.toLowerCase())
  );

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImageUploading(true);
    try {
      const uploadResult = await uploadToCloudinary(file, 'image', 'artworks');
      const imageUrl = uploadResult.url;
      console.log('Uploaded image URL:', imageUrl, 'Type:', typeof imageUrl);
      setFormData(prev => ({
        ...prev,
        cover_image: imageUrl
      }));
      setMessage({ type: 'success', text: 'Cover image uploaded successfully!' });
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage({ type: 'error', text: 'Failed to upload image' });
    } finally {
      setImageUploading(false);
    }
  };

  const handleAudioUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setAudioUploading(true);
    try {
      const uploadResult = await uploadToCloudinary(file, 'video', 'audio'); // Use video preset for audio
      const audioUrl = uploadResult.url;
      setAudioFile({ file, url: audioUrl });
      setMessage({ type: 'success', text: 'Audio story uploaded successfully!' });
    } catch (error) {
      console.error('Error uploading audio:', error);
      setMessage({ type: 'error', text: 'Failed to upload audio' });
    } finally {
      setAudioUploading(false);
    }
  };

  const handleAdditionalImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setImageUploading(true);
    try {
      const uploadPromises = files.map(file => uploadToCloudinary(file, 'image', 'artworks'));
      const uploadResults = await Promise.all(uploadPromises);
      const imageUrls = uploadResults.map(result => result.url);
      
      const newImages = imageUrls.map(url => ({ url }));
      setAdditionalImages(prev => [...prev, ...newImages]);
      setMessage({ type: 'success', text: `${files.length} image(s) uploaded successfully!` });
    } catch (error) {
      console.error('Error uploading images:', error);
      setMessage({ type: 'error', text: 'Failed to upload images' });
    } finally {
      setImageUploading(false);
    }
  };

  const removeAdditionalImage = (index) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeAudioFile = () => {
    setAudioFile(null);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(dragCounter + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(dragCounter - 1);
    if (dragCounter - 1 === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        // Create a fake event object to reuse the existing upload logic
        const fakeEvent = {
          target: {
            files: [file]
          }
        };
        handleImageUpload(fakeEvent);
      } else {
        setMessage({ type: 'error', text: 'Please upload an image file' });
      }
      e.dataTransfer.clearData();
    }
  };

  // Additional images drag and drop handlers
  const handleAdditionalDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounterAdditional(dragCounterAdditional + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDraggingAdditional(true);
    }
  };

  const handleAdditionalDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounterAdditional(dragCounterAdditional - 1);
    if (dragCounterAdditional - 1 === 0) {
      setIsDraggingAdditional(false);
    }
  };

  const handleAdditionalDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleAdditionalDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAdditional(false);
    setDragCounterAdditional(0);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
      if (files.length > 0) {
        // Create a fake event object to reuse the existing upload logic
        const fakeEvent = {
          target: {
            files: files
          }
        };
        handleAdditionalImageUpload(fakeEvent);
      } else {
        setMessage({ type: 'error', text: 'Please upload image files only' });
      }
      e.dataTransfer.clearData();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: 'Title is required' });
      return;
    }

    setLoading(true);
    try {
      // Create the artwork record in vcs table
      const artworkData = {
        uuid: userId, // UUID of the artist
        title: formData.title,
        description: formData.medium || '', // Store medium in description field
        width: formData.width ? parseFloat(formData.width) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        price: formData.price ? parseFloat(formData.price) : null,
        status: formData.status || 'For Sale',
        location: formData.location || null, // gallery_id
        cover_image: formData.cover_image || null,
        mode: 'Classic',
        access_control: 'Public',
        more: {
          process: formData.process || '',
          inspiration: formData.inspiration || ''
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: artwork, error: artworkError } = await supabase
        .from('vcs')
        .insert([artworkData])
        .select()
        .single();

      if (artworkError) throw artworkError;

      // Now we have the artwork with vc_id, add media elements if any
      const mediaElements = [];

      // Add audio story if uploaded
      if (audioFile) {
        mediaElements.push({
          vc_id: artwork.vc_id,
          type: 'Audio',
          source: audioFile.url,
          artist_uuid: userId,
          created_at: new Date().toISOString()
        });
      }

      // Add additional images
      additionalImages.forEach(image => {
        mediaElements.push({
          vc_id: artwork.vc_id,
          type: 'Image',
          source: image.url,
          artist_uuid: userId,
          created_at: new Date().toISOString()
        });
      });

      if (mediaElements.length > 0) {
        const { error: mediaError } = await supabase
          .from('media_elements')
          .insert(mediaElements);

        if (mediaError) throw mediaError;
      }

      setMessage({ type: 'success', text: 'Artwork created successfully!' });
      
      // Navigate back to user detail page after a short delay
      setTimeout(() => {
        navigate(`/admin/users/${userId}`);
      }, 2000);

    } catch (error) {
      console.error('Error creating artwork:', error);
      setMessage({ type: 'error', text: 'Failed to create artwork' });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (!artist) {
    return <div className="loading">Loading artist information...</div>;
  }

  return (
    <div className="add-artwork-page">
      <div className="page-header">
        <button 
          className="back-btn"
          onClick={() => navigate(`/admin/users/${userId}`)}
        >
          <i className="fas fa-arrow-left"></i>
          Back to {artist.first_name} {artist.last_name}
        </button>
        <h1>Add New Artwork</h1>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      <div className="content-container">
        <form onSubmit={handleSubmit} className="artwork-form">
          
          {/* Upload Artwork Image Section - First */}
          <div className="artwork-form-section">
            <h3>Upload Artwork Image</h3>
            <div className="image-upload-section compact">
              {formData.cover_image ? (
                <div className="image-container compact">
                  <img 
                    src={formData.cover_image} 
                    alt="Cover" 
                    className="artwork-image compact"
                    onLoad={() => console.log('Image loaded successfully')}
                    onError={(e) => console.error('Image failed to load:', e)}
                  />
                  <div className="image-overlay">
                    <label className="image-upload-btn">
                      <i className="fas fa-camera"></i>
                      <span>Change Image</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                        disabled={imageUploading}
                      />
                    </label>
                  </div>
                  {imageUploading && (
                    <div className="upload-overlay">
                      <div className="spinner"></div>
                    </div>
                  )}
                </div>
              ) : (
                <div 
                  className={`image-placeholder compact ${isDragging ? 'drag-over' : ''}`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <label className="upload-placeholder">
                    <i className="fas fa-image"></i>
                    <span>
                      {isDragging ? 'Drop image here' : 'Upload Artwork Image'}
                    </span>
                    <small>
                      {isDragging ? '' : 'Drag & drop or click to select'}
                    </small>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                      disabled={imageUploading}
                    />
                  </label>
                  {imageUploading && (
                    <div className="upload-overlay">
                      <div className="spinner"></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Basic Information */}
          <div className="artwork-form-section">
            <h3>Basic Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter artwork title"
                  required
                />
              </div>

              <div className="form-group">
                <label>Year</label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => handleInputChange('year', parseInt(e.target.value))}
                  placeholder="Year created"
                />
              </div>

              <div className="form-group">
                <label>Width (inches)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.width}
                  onChange={(e) => handleInputChange('width', e.target.value)}
                  placeholder="Width"
                />
              </div>

              <div className="form-group">
                <label>Height (inches)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.height}
                  onChange={(e) => handleInputChange('height', e.target.value)}
                  placeholder="Height"
                />
              </div>

              <div className="form-group">
                <label>Price</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div className="form-group">
                <label>Medium</label>
                <input
                  type="text"
                  value={formData.medium}
                  onChange={(e) => handleInputChange('medium', e.target.value)}
                  placeholder="e.g., Oil on canvas, Digital art"
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <option value="For Sale">For Sale</option>
                  <option value="Sold">Sold</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Not for Sale">Not for Sale</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label>Process</label>
                <textarea
                  value={formData.process}
                  onChange={(e) => handleInputChange('process', e.target.value)}
                  placeholder="Describe your creative process..."
                  rows="3"
                />
              </div>

              <div className="form-group full-width">
                <label>Inspiration</label>
                <textarea
                  value={formData.inspiration}
                  onChange={(e) => handleInputChange('inspiration', e.target.value)}
                  placeholder="What inspired this artwork..."
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Gallery Location</label>
                <div className="gallery-search-container">
                  <div className="search-input-wrapper">
                    <i className="fas fa-search search-icon"></i>
                    <input
                      type="text"
                      value={gallerySearch}
                      onChange={(e) => handleGallerySearch(e.target.value)}
                      onFocus={() => setShowGalleryDropdown(true)}
                      placeholder="Search for a gallery..."
                    />
                    {gallerySearch && (
                      <i 
                        className="fas fa-times clear-icon" 
                        onClick={() => {
                          setGallerySearch('');
                          setSelectedGallery(null);
                          setFormData(prev => ({ ...prev, location: null }));
                          setShowGalleryDropdown(false);
                        }}
                      ></i>
                    )}
                  </div>
                  {showGalleryDropdown && gallerySearch && (
                    <div className="gallery-dropdown">
                      {filteredGalleries.length > 0 ? (
                        filteredGalleries.slice(0, 10).map((gallery) => (
                          <div
                            key={gallery.gallery_id}
                            className="gallery-option"
                            onClick={() => handleGallerySelect(gallery)}
                          >
                            <div className="gallery-name">{gallery.name}</div>
                            <div className="gallery-meta">
                              <span className="gallery-type">{gallery.type}</span>
                              {gallery.address && (
                                <span className="gallery-address">{gallery.address}</span>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="gallery-option">No galleries found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Audio Story Section */}
          <div className="artwork-form-section">
            <h3>Audio Story</h3>
            <div className="media-section">
              {audioFile ? (
                <div className="media-items">
                  <div className="media-item audio-item">
                    <div className="audio-info">
                      <div className="media-info">
                        <i className="fas fa-volume-up" style={{fontSize: '16px', color: '#8b5cf6'}}></i>
                        <span>Audio Story</span>
                        <span className="media-date">Just uploaded</span>
                      </div>
                      <div className="media-actions">
                        <button
                          type="button"
                          className="media-action-btn delete-btn"
                          onClick={removeAudioFile}
                          title="Remove audio"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <div className="audio-player-container">
                      <audio 
                        controls 
                        className="audio-player"
                        preload="metadata"
                      >
                        <source src={audioFile.url} type="audio/mpeg" />
                        <source src={audioFile.url} type="audio/mp4" />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="upload-section">
                  <label className="upload-btn">
                    <i className="fas fa-microphone"></i>
                    <span>{audioUploading ? 'Uploading...' : 'Upload Audio Story'}</span>
                    <input
                      type="file"
                      accept="audio/*,.mp3,.m4a"
                      onChange={handleAudioUpload}
                      style={{ display: 'none' }}
                      disabled={audioUploading}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Additional Images Section */}
          <div className="artwork-form-section">
            <h3>Additional Images</h3>
            <div className="media-section">
              {additionalImages.length > 0 && (
                <div className="media-items">
                  {additionalImages.map((image, index) => (
                    <div key={index} className="media-item image-item">
                      <div className="media-info">
                        <i className="fas fa-image" style={{fontSize: '16px', color: '#06b6d4'}}></i>
                        <span>Additional Image {index + 1}</span>
                        <span className="media-date">Just uploaded</span>
                      </div>
                      <div className="media-actions">
                        <a 
                          href={image.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="media-action-btn view-btn"
                          title="View image"
                        >
                          <i className="fas fa-eye"></i>
                        </a>
                        <button
                          type="button"
                          className="media-action-btn delete-btn"
                          onClick={() => removeAdditionalImage(index)}
                          title="Remove image"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div 
                className={`upload-section ${isDraggingAdditional ? 'drag-over' : ''}`}
                onDragEnter={handleAdditionalDragEnter}
                onDragLeave={handleAdditionalDragLeave}
                onDragOver={handleAdditionalDragOver}
                onDrop={handleAdditionalDrop}
              >
                <label className="upload-btn">
                  <i className="fas fa-images"></i>
                  <span>
                    {imageUploading ? 'Uploading...' : 
                     isDraggingAdditional ? 'Drop images here' : 
                     'Upload Additional Images'}
                  </span>
                  {!isDraggingAdditional && (
                    <small>Drag & drop multiple images or click to select</small>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleAdditionalImageUpload}
                    style={{ display: 'none' }}
                    disabled={imageUploading}
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate(`/admin/users/${userId}`)}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || !formData.title.trim()}
            >
              {loading ? (
                <>
                  <div className="spinner small"></div>
                  Creating...
                </>
              ) : (
                <>
                  <i className="fas fa-plus"></i>
                  Create Artwork
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddArtworkPage;